import { Injectable } from '@angular/core';
import { LoyaltyProgramApiService } from '@features/loyalty-program/services/loyalty-program-api/loyalty-program-api.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SentryService } from '@rd-lib/sentry/sentry.service';
import { NydusNetworkDataError } from '@rd/errors/nydus-network-data-error';
import { HttpError, LoyaltyProgram } from '@rd/types';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as actions from '../actions/loyalty-program.actions';

@Injectable()
export class LoyaltyProgramEffects {
  constructor(
    private actions$: Actions,
    private loyaltyProgramApiService: LoyaltyProgramApiService,
    private sentryService: SentryService,
  ) {}

  loadLoyaltyPrograms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoyaltyProgramActionTypes.LOAD_LOYALTY_PROGRAMS),
      switchMap(() => {
        return this.loyaltyProgramApiService.getLoyaltyPrograms().pipe(
          map((loyaltyPrograms: LoyaltyProgram[]) => {
            this.checkLoyaltyProgramsIntegrity(loyaltyPrograms);
            return new actions.LoadLoyaltyProgramsSuccess(loyaltyPrograms);
          }),
          catchError((error: HttpError) =>
            of(new actions.LoadLoyaltyProgramsFail(error)),
          ),
        );
      }),
    ),
  );

  loadSingleLoyaltyProgram$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoyaltyProgramActionTypes.LOAD_SINGLE_LOYALTY_PROGRAM),
      switchMap((action: actions.LoadSingleLoyaltyProgram) => {
        return this.loyaltyProgramApiService
          .getLoyaltyProgram(action.payload)
          .pipe(
            map(
              (loyaltyProgram: LoyaltyProgram) =>
                new actions.LoadSingleLoyaltyProgramSuccess(loyaltyProgram),
            ),
            catchError((error: HttpError) =>
              of(new actions.LoadSingleLoyaltyProgramFail(error)),
            ),
          );
      }),
    ),
  );

  private checkLoyaltyProgramsIntegrity(
    loyaltyPrograms: LoyaltyProgram[],
  ): void {
    if (loyaltyPrograms.length === 0) {
      const nydusNetworkDataError = new NydusNetworkDataError({
        httpMethod: 'GET',
        endpoint: '/loyalty_programs',
        message: 'LoyaltyPrograms are empty.',
      });

      this.sentryService.manualError(nydusNetworkDataError.toString());
    }
  }
}
